import { z } from "zod";
import { EntityBase, Instant, Name, Note, toAsciiDigits } from "./common.js";
import { Id } from "./ids.js";
import { MobilePhone } from "./phone.js";

/**
 * Identity contracts (ADR-29): one person per phone, two doors, SMS only for device enrolment,
 * user-set PIN for every unlock, sessions always carry a context.
 */

/** Which door a person came through / which hat a session wears. */
export const Context = z.enum(["customer", "staff"]);
export type Context = z.infer<typeof Context>;

export const Person = EntityBase.extend({
  phone: MobilePhone,
  displayName: Name,
  /** Set only by a manager adding the phone to the staff list; a customer never becomes staff by themself. */
  isStaff: z.boolean(),
});
export type Person = z.infer<typeof Person>;

export const DevicePlatform = z.enum(["web", "android", "ios-pwa", "hub", "station"]);
export const DeviceStatus = z.enum(["pending-approval", "active", "revoked"]);

export const Device = EntityBase.extend({
  /** Null for a shared kitchen station account. */
  personId: Id.nullable(),
  label: Name,
  platform: DevicePlatform,
  status: DeviceStatus,
  /** Senior-manager approval for devices of sensitive roles. */
  approvedById: Id.nullable(),
  lastVerifiedAt: Instant.nullable(),
});
export type Device = z.infer<typeof Device>;

// ---------- enrolment (SMS) ----------

export const OtpPurpose = z.enum(["enroll", "reverify", "reset"]);

export const OtpRequest = z.object({
  door: Context,
  phone: MobilePhone,
  purpose: OtpPurpose,
  deviceLabel: Name,
  platform: DevicePlatform.exclude(["station"]),
});
export type OtpRequest = z.infer<typeof OtpRequest>;

/** Six ASCII digits; Persian digits typed on a phone keypad are accepted and converted. */
export const OtpCode = z
  .string()
  .transform((s) => toAsciiDigits(s).trim())
  .pipe(z.string().regex(/^\d{6}$/, "کد شش‌رقمی را وارد کنید"));

export const OtpVerify = z.object({ challengeId: Id, code: OtpCode });
export type OtpVerify = z.infer<typeof OtpVerify>;

/** Outcome of the customer door for a verified phone (ADR-29 §2). */
export const CustomerDoorOutcome = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("unit-contact"), orderingUnitIds: z.array(Id).min(1) }),
  z.object({ kind: z.literal("subscriber") }),
]);

/** Outcome of the staff door: unknown numbers create nothing. */
export const StaffDoorOutcome = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("staff"), needsDeviceApproval: z.boolean() }),
  z.object({ kind: z.literal("not-on-staff-list") }),
]);

// ---------- PIN ----------

const WEAK_PINS = new Set(["0000", "1111", "1234", "4321", "000000", "111111", "123456", "654321", "123123"]);

/** Builds a PIN schema of the required length (4, or 6 for sensitive roles). The user chooses it, never a manager. */
export function pinSchema(length: 4 | 6) {
  // Phone keyboards type Persian or Arabic digits; the PIN is the same number either way (AG-02 finding).
  return z
    .string()
    .transform((p) => toAsciiDigits(p).trim())
    .pipe(
      z
        .string()
        .regex(new RegExp(`^\\d{${String(length)}}$`), `PIN باید ${length === 4 ? "۴" : "۶"} رقم باشد`)
        .refine(
          (p) => !WEAK_PINS.has(p) && !/^(\d)\1+$/.test(p),
          "این PIN خیلی ساده است؛ عدد دیگری انتخاب کنید",
        ),
    );
}

export const PinSetup = z.object({ deviceId: Id, pin: z.string() });
export const PinUnlock = z.object({ deviceId: Id, pin: z.string() });

export const Session = z.object({
  id: Id,
  personId: Id,
  deviceId: Id,
  context: Context,
  expiresAt: Instant,
});
export type Session = z.infer<typeof Session>;

// ---------- roles = permissions × scope × switches ----------

/** `module.resource` registered by a module manifest, e.g. `orders.order`. */
export const PermissionResource = z.string().regex(/^[a-z][a-z0-9-]*\.[a-z][a-z0-9-]*$/);
export const PermissionAction = z.enum(["view", "create", "update", "approve"]);
export type PermissionAction = z.infer<typeof PermissionAction>;

export const PermissionGrant = z.object({
  resource: PermissionResource,
  actions: z.array(PermissionAction).min(1),
});

export const Scope = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("all") }),
  z.object({ kind: z.literal("organizations"), ids: z.array(Id).min(1) }),
  z.object({ kind: z.literal("sites"), ids: z.array(Id).min(1) }),
  z.object({ kind: z.literal("warehouses"), ids: z.array(Id).min(1) }),
  z.object({ kind: z.literal("events"), ids: z.array(Id).min(1) }),
  /** A contact's own ordering unit(s); resolved from the person's contact links. */
  z.object({ kind: z.literal("own-units") }),
]);
export type Scope = z.infer<typeof Scope>;

/** Per-role switches (architecture §10.4); absent = off. */
export const RoleSwitches = z.object({
  seePrices: z.boolean().default(false),
  seeCost: z.boolean().default(false),
  addOrderLines: z.boolean().default(false),
});

export const Role = EntityBase.extend({
  name: Name,
  description: Note.default(""),
  /** Which hat this role belongs to. A customer-context role can never grant staff resources. */
  context: Context,
  /** Sensitive roles (manager, finance, HR): 6-digit PIN + senior-manager device approval. */
  sensitive: z.boolean(),
  permissions: z.array(PermissionGrant),
  scope: Scope,
  switches: RoleSwitches,
});
export type Role = z.infer<typeof Role>;

export const RoleInput = Role.pick({
  name: true,
  description: true,
  context: true,
  sensitive: true,
  permissions: true,
  scope: true,
  switches: true,
});

export const RoleAssignment = EntityBase.extend({
  personId: Id,
  roleId: Id,
  /** Temporary deputy assignments expire (architecture §10.4). */
  validUntil: Instant.nullable(),
});
export type RoleAssignment = z.infer<typeof RoleAssignment>;

// ---------- order links (ADR-30, F3-T09) ----------

/** A link token as it appears in the address `/o/<token>`: 256 random bits, base64url. */
export const LinkToken = z.string().regex(/^[A-Za-z0-9_-]{43}$/, "لینک معتبر نیست");

/** Public start of a link on a new device. The token travels in the body, never in the URL or logs. */
export const LinkStart = z.object({
  token: LinkToken,
  deviceLabel: Name,
  platform: DevicePlatform.exclude(["station"]),
});
export type LinkStart = z.infer<typeof LinkStart>;

/** Manager: issue (or rotate) the order link of a contact. Issuing always revokes the contact's previous link. */
export const OrderLinkIssue = z.object({ contactId: Id });
export type OrderLinkIssue = z.infer<typeof OrderLinkIssue>;

/** A link as managers see it: never the token itself, only its state. */
export const OrderLink = EntityBase.extend({
  contactId: Id,
  expiresAt: Instant,
  revokedAt: Instant.nullable(),
  revokeReason: z.string().nullable(),
  issuedById: Id.nullable(),
});
export type OrderLink = z.infer<typeof OrderLink>;

export const StaffMember = z.object({
  id: Id,
  displayName: Name,
  phone: MobilePhone,
  hasPin: z.boolean(),
  isSupport: z.boolean().default(false),
});
export type StaffMember = z.infer<typeof StaffMember>;
